import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { useRouteMatch } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import * as Sentry from '@sentry/browser';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import CoachIdentity from '../../../../../Model/CoachIdentity';

import {
  Container,
  ConfigContainer,
  Title,
  SubTitle,
  StyledTextField,
  StyledButton,
  HeaderContainer,
} from './styles';

import texts from './texts.json';

const AIPromptSettings = () => {
  const { params: { userId: coachId } } = useRouteMatch();
  const { userId: loggedInUserId } = useSessionStore();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const [smartCheckInPrompt, setSmartCheckInPrompt] = useState('');
  const [smartContextPrompt, setSmartContextPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [coachIdentityDoc, setCoachIdentityDoc] = useState(null);

  // Load coach identity document on mount
  useEffect(() => {
    const loadCoachIdentity = async () => {
      setIsLoading(true);
      try {
        const coachIdentity = await CoachIdentity.getCoachIdentity(coachId);

        if (isComponentMountedRef.current) {
          setCoachIdentityDoc(coachIdentity);

          if (coachIdentity && coachIdentity.exists) {
            // Load existing values if they exist
            setSmartCheckInPrompt(coachIdentity.checkInStylePrompt || '');
            setSmartContextPrompt(coachIdentity.smartContextStylePrompt || '');
          }
          setIsLoading(false);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          Sentry.captureException(error, {
            extra: {
              coachId,
              message: texts.loadError,
            },
          });
          setIsLoading(false);
        }
      }
    };

    loadCoachIdentity();
  }, [coachId, isComponentMountedRef]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      const coachIdentityData = {
        checkInStylePrompt: smartCheckInPrompt,
        smartContextStylePrompt: smartContextPrompt,
        lastUpdatedAt: new Date(),
        lastUpdatedBy: loggedInUserId,
      };

      if (coachIdentityDoc && coachIdentityDoc.exists) {
        // Update existing document
        await coachIdentityDoc.update(coachIdentityData);
      } else {
        // Create new document
        const newCoachIdentity = new CoachIdentity(coachId);
        await newCoachIdentity.set({
          ...coachIdentityData,
          coachId,
          createdAt: new Date(),
        }, { merge: true });
      }

      if (isComponentMountedRef.current) {
        showToast(texts.settingsSaved);
        setIsLoading(false);
      }
    } catch (error) {
      if (isComponentMountedRef.current) {
        Sentry.captureException(error, {
          extra: {
            coachId,
            message: texts.saveError,
          },
        });
        showToast(texts.saveError, { error: true });
        setIsLoading(false);
      }
    }
  }, [
    coachIdentityDoc,
    smartCheckInPrompt,
    smartContextPrompt,
    coachId,
    loggedInUserId,
    isComponentMountedRef,
    showToast,
  ]);

  const handleSmartCheckInChange = useCallback((event) => {
    setSmartCheckInPrompt(event.target.value);
  }, []);

  const handleSmartContextChange = useCallback((event) => {
    setSmartContextPrompt(event.target.value);
  }, []);

  return (
    <Container>
      <ConfigContainer>
        <HeaderContainer>
          <Title>{texts.smartCheckInTitle}</Title>
          <SubTitle>{texts.smartCheckInDescription}</SubTitle>
        </HeaderContainer>
        <StyledTextField
          value={smartCheckInPrompt}
          onChange={handleSmartCheckInChange}
          placeholder={texts.smartCheckInPlaceholder}
          multiline
          rows={10}
          variant="outlined"
        />
      </ConfigContainer>

      <ConfigContainer>
        <HeaderContainer>
          <Title>{texts.smartContextTitle}</Title>
          <SubTitle>{texts.smartContextDescription}</SubTitle>
        </HeaderContainer>
        <StyledTextField
          value={smartContextPrompt}
          onChange={handleSmartContextChange}
          placeholder={texts.smartContextPlaceholder}
          multiline
          rows={10}
          variant="outlined"
        />
      </ConfigContainer>

      <StyledButton
        variant="contained"
        onClick={handleSave}
        startIcon={<SaveIcon />}
      >
        {texts.saveButton}
      </StyledButton>

      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

export default compose(
  observer,
)(AIPromptSettings);
